import type { AlertWorkerApi } from './types';
import * as Comlink from 'comlink';
import type { Remote } from 'comlink';
import { injectable, inject } from 'inversify';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Provider for the AlertService worker.
 *
 * In SharedWorker mode, each userId gets its own SharedWorker instance
 * (via the `name` option). Multiple tabs for the same user share one worker;
 * different users get completely isolated workers with separate state.
 *
 * In dedicated Worker mode (fallback), a single Worker is used.
 */
@injectable()
export class AlertServiceProvider {
    #log: ReactBindings['Logger'];
    /** Dedicated worker instance (non-SharedWorker fallback). Created lazily. */
    #dedicatedWorker: Worker | null = null;
    #dedicatedProxy: Remote<AlertWorkerApi> | null = null;

    constructor(@inject('Logger') logger: ReactBindings['Logger']) {
        this.#log = logger.getSubLogger({ name: 'AlertServiceProvider' });
    }

    /**
     * Get a Comlink-wrapped proxy for the given user's worker.
     *
     * For SharedWorker: creates (or connects to) a SharedWorker named
     * `alert-worker-${userId}`. Tabs with the same userId share one worker.
     *
     * For dedicated Worker: returns a cached single-user proxy (userId ignored).
     */
    getWorkerProxy(userId: string): Remote<AlertWorkerApi> {
        if (typeof SharedWorker !== 'undefined') {
            const workerName = `alert-worker-${userId}`;
            if (TRACE) {
                this.#log.trace('Creating SharedWorker connection', { userId, workerName });
            }
            const worker = new SharedWorker(new URL('./worker/index.ts', import.meta.url), {
                type: 'module',
                name: workerName,
            });
            worker.port.start();
            return Comlink.wrap<AlertWorkerApi>(worker.port);
        } else {
            if (!this.#dedicatedWorker) {
                if (TRACE) {
                    this.#log.trace('Creating dedicated Worker');
                }
                this.#dedicatedWorker = new Worker(new URL('./worker/index.ts', import.meta.url), { type: 'module' });
                this.#dedicatedProxy = Comlink.wrap<AlertWorkerApi>(this.#dedicatedWorker);
            }
            return this.#dedicatedProxy!;
        }
    }
}
